// src/components/survey/post-drive-survey.tsx
'use client';

import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Skeleton } from '@/components/ui/skeleton';
import { usePreRegEvent, useSubmitSurvey } from '@/hooks';
import { initializeSurveyForBrand } from '@latitude-digital/survey-core';
import { mapToLincolnPayload } from '@latitude-digital/survey-core/mappers';
import { AlertCircle } from 'lucide-react';
import { useRouter } from 'next/navigation';
import { useEffect, useMemo, useState } from 'react';
import { Model } from 'survey-core';
import { Survey } from 'survey-react-ui';
import surveyJSON from './json/post-test-drive.json';

export function PostDriveSurvey() {
  const router = useRouter();

  // Extract hash and optional event_portal from the browser URL
  // Route: /post/[hash]/[[...event_portal]]
  const [hash, setHash] = useState<string | null>(null);
  const [eventPortal, setEventPortal] = useState<string | undefined>();

  useEffect(() => {
    const segments = window.location.pathname.split('/').filter(Boolean);
    // segments: ['post', hash] or ['post', hash, event_portal]
    const extractedHash = segments[1] ?? null;
    if (extractedHash && extractedHash !== 'placeholder') {
      setHash(extractedHash); // eslint-disable-line react-hooks/set-state-in-effect
    }
    const extractedPortal = segments[2];
    if (extractedPortal) {
      setEventPortal(extractedPortal);
    }
  }, []);

  // Fetch event info (event_id + event_name) by hash
  const { data: eventData, isLoading, isError, error } = usePreRegEvent(hash ?? '');

  // Build redirect paths
  const eventTypeParam = eventData?.event_type ? `?event_type=${eventData.event_type}` : '';
  const successPath = eventPortal
    ? `/submit/success/${eventPortal}${eventTypeParam}`
    : `/submit/success${eventTypeParam}`;
  const errorPath = eventPortal ? `/submit/error/${eventPortal}` : '/submit/error';

  const accessKey = process.env.NEXT_PUBLIC_GTB_ACCESS_KEY;

  // Survey submission mutation
  const submitSurvey = useSubmitSurvey(accessKey ?? '', {
    onSuccess: () => router.push(successPath),
    onError: () => router.push(errorPath),
  });

  // Create SurveyJS model
  const survey = useMemo(() => {
    const model = new Model(surveyJSON);

    initializeSurveyForBrand(model, { brand: 'Lincoln' });

    // Required for FDS radio button styling
    (model as Record<string, unknown>).__isFDSBrand = true;

    model.showCompletedPage = false;
    model.focusFirstQuestionAutomatic = false;
    model.questionErrorLocation = 'bottom';
    model.widthMode = 'responsive';

    // Inject metadata into survey data when survey first renders
    model.onAfterRenderSurvey.add((sender) => {
      sender.mergeData({
        start_time: new Date().toISOString(),
        survey_date: new Date().toISOString(),
        event_id: String(eventData?.event_id ?? ''),
        app_version: 'surveyjs_2.0',
        device_survey_guid: crypto.randomUUID(),
        abandoned: 0,
        _utm: new URLSearchParams(window.location.search).toString(),
        _referrer: window.frames?.top?.document?.referrer,
        language: sender.locale || 'en',
        _language: window.navigator?.language,
        _screenWidth: window.screen?.width,
        _offset: new Date().getTimezoneOffset(),
        _timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      });
    });

    return model;
  }, [eventData?.event_id]);

  // Set event_name variable from API response
  useEffect(() => {
    if (eventData?.event_name && survey) {
      survey.setVariable('event_name', eventData.event_name);
    }
  }, [eventData, survey]);

  // Handle survey completion
  useEffect(() => {
    const onComplete = (sender: Model) => {
      sender.mergeData({ end_time: new Date().toISOString() });

      const payload = mapToLincolnPayload(sender, sender.data, {
        lincolnEventID: String(eventData?.event_id ?? ''),
        surveyType: 'postTD',
      });

      payload.custom_data = JSON.stringify({
        _utm: payload._utm,
        _referrer: payload._referrer,
        _language: payload._language,
        _screenWidth: payload._screenWidth,
        _offset: payload._offset,
        _timeZone: payload._timeZone,
      });

      submitSurvey.mutate(payload);
    };
    survey.onComplete.add(onComplete);
    return () => survey.onComplete.remove(onComplete);
  }, [survey, eventData?.event_id, submitSurvey]);

  if (!hash || isLoading) {
    return (
      <div className="mx-auto max-w-2xl space-y-6">
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-96 w-full" />
      </div>
    );
  }

  if (isError || !eventData) {
    return (
      <div className="mx-auto max-w-2xl">
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Error</AlertTitle>
          <AlertDescription>
            {error?.message || 'Unable to load survey. Please check the URL and try again.'}
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  if (submitSurvey.isPending) {
    return (
      <div className="flex min-h-[40vh] items-center justify-center">
        <div className="text-center space-y-4">
          <div className="mx-auto h-8 w-8 animate-spin rounded-full border-4 border-muted border-t-primary" />
          <p className="text-muted-foreground">Submitting your survey...</p>
        </div>
      </div>
    );
  }

  return (
    <div id="fd-nxt" className="lincoln_light">
      <Survey model={survey} />
    </div>
  );
}
